import React, { createContext, useContext, useState, useEffect } from "react";

const SettingsContext = createContext(null);

const API = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

// Generic placeholders — shown until a hotel's own branding loads (or if it
// never installed/configured any), so no page ever renders empty/broken.
const DEFAULT_SETTINGS = {
  hotelName:  "Your Hotel",
  email:      "",
  phone:      "",
  phone2:     "",
  address:    "",
  timezone:   "Asia/Kolkata",
  currency:   "INR",
  country:    "",
  logoUrl:    "",
  heroImages: [],
  aboutText:  "",
  faqs:       [],
  customFields: [],
  upiId:         "",
  upiQrImageUrl: "",
  mapsUrl:         "",
  mapsLat:         "",
  mapsLng:         "",
  googleReviewUrl: "",
  crossPromoName:  "",
  crossPromoUrl:   "",
  guestPolicyText: "",
  // Whether the public booking form / guest registration form show the ID
  // proof upload section at all — some tenants require it, some ask us to
  // hide it entirely. Default true preserves the existing form for every
  // tenant who hasn't touched this.
  idProofUploadEnabled: true,
  // Whether OTA guests are sent the ID-upload link automatically.
  sendIdUploadLink: true,
  // Online payment gateway (services/paymentGateway.js) — `enabled` is
  // false whenever no gateway is configured for this tenant (or the
  // feature is off), in which case payLaterEnabled is meaningless (there
  // is nothing to pay online, so nothing to require).
  themeId: "amber",
  themeVars: null,
  payments: { enabled: false, keyId: "", payLaterEnabled: true },
  // Falls back to exactly this app's original hardcoded amber/orange look
  // (see tailwind.config.js's `amber` palette) — a tenant with no theme
  // configured yet must render identically to before theming existed,
  // per CLAUDE.md's "without changing how it looks."
  theme: {
    primaryColor: "#F59E0B",
    accentColor:  "#EA580C",
    surfaceColor: "#FFFFFF",
    logoUrl:      "",
    faviconUrl:   "",
    brandName:    "",
    // Which landing-page design renders at "/" — see LandingPageSwitcher.jsx.
    // Defaults to the original design so nothing changes for a tenant whose
    // super admin hasn't picked one.
    templateId:   "classic",
  },
};

// CSS custom properties, kept in sync with the tenant's chosen palette.
//
// `themeVars` is the whole primary/accent ramp, resolved server-side from
// the preset the tenant picked (backend/config/themePresets.js — the one
// source of truth, shared with the invoice renderer). tailwind.config.js
// declares every `amber-*`/`orange-*` shade as rgb(var(--c-…)), so setting
// these retints the ~600 existing usages across the app at once, rather
// than only the handful of components that were ever migrated to
// var(--color-primary) by hand.
//
// The three --color-* vars below are kept for those already-migrated
// components (LoginPage, PwaInstallPrompt) so nothing regresses.
function applyThemeVars(theme, themeVars) {
  const root = document.documentElement.style;
  for (const [name, value] of Object.entries(themeVars || {})) root.setProperty(name, value);
  root.setProperty("--color-primary", theme.primaryColor || DEFAULT_SETTINGS.theme.primaryColor);
  root.setProperty("--color-accent",  theme.accentColor  || DEFAULT_SETTINGS.theme.accentColor);
  root.setProperty("--color-surface", theme.surfaceColor || DEFAULT_SETTINGS.theme.surfaceColor);
}

export function SettingsProvider({ children }) {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [loaded, setLoaded]     = useState(false);

  async function load() {
    try {
      const res  = await fetch(`${API}/settings/public`);
      const json = await res.json();
      if (json.success) {
        const merged = { ...DEFAULT_SETTINGS, ...json.data, theme: { ...DEFAULT_SETTINGS.theme, ...json.data.theme } };
        setSettings(merged);
        applyThemeVars(merged.theme, json.data.themeVars);
        if (json.data.theme?.brandName || json.data.hotelName) document.title = json.data.theme?.brandName || json.data.hotelName;
      }
    } catch {
      // Not installed yet / server unreachable — keep defaults (already applied below).
    } finally {
      setLoaded(true);
    }
  }

  useEffect(() => {
    applyThemeVars(DEFAULT_SETTINGS.theme); // paint the default look immediately, before the fetch resolves
    load();
  }, []);

  // Exposed so the admin Settings page can pull fresh data right after
  // saving, instead of the whole app waiting for its own next mount.
  return (
    <SettingsContext.Provider value={{ ...settings, loaded, refresh: load }}>
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings() {
  return useContext(SettingsContext);
}
